import { cx } from 'class-variance-authority';
import { HTMLAttributes, ReactNode } from 'react';
import Checkbox from './Table/Checkbox';

// Typed against the base element, not the div: the single-select variant renders a
// button, and both accept the shared attributes.
interface Props extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
    selected?: boolean;
    onSelect?: () => void;
    /** Lighter card chrome. Ignored in `single` mode, which never carries a shadow. */
    flat?: boolean;
    title?: ReactNode;
    /**
     * How many of these can be picked at once.
     *
     * `multi` (the default, and what every existing caller gets) shows a checkbox.
     * `single` is one of a set: no checkbox, because a checkbox next to four cards of
     * which exactly one can win says the wrong thing — the card itself carries the
     * state, and it is a real radio for the keyboard.
     */
    selectionMode?: 'multi' | 'single';
    /** Tight padding, for dense grids of small choices rather than a handful of big ones. */
    compact?: boolean;
    /** Single mode only: shown but not selectable. */
    disabled?: boolean;
}

/**
 * A card you choose.
 *
 * One component for every "pick one of these" and "pick some of these" in the app, so
 * a choice looks the same whether it is a pricing rule, a design template or a
 * typeface. The green edge is the selected state throughout.
 */
const SelectableCard = ({
    flat,
    title,
    selected,
    onSelect,
    selectionMode = 'multi',
    compact,
    disabled,
    children,
    className,
    ...props
}: Props) => {
    const base = cx(flat ? 'tw-card-flat' : 'tw-card', onSelect && 'tw-selectable');

    if (selectionMode === 'single') {
        return (
            // A button, not a div: one-of-N is reachable by keyboard, and the content of
            // such a card is display only. The multi variant stays a div because its
            // children are form fields, which a button may not contain.
            <button
                type="button"
                role="radio"
                aria-checked={Boolean(selected)}
                disabled={disabled}
                onClick={() => onSelect?.()}
                className={cx(
                    // The border is stated outright rather than taken from `tw-selectable`,
                    // whose transparent edge wins over the card's own and leaves an
                    // unselected tile with no boundary at all — a grid of twelve then
                    // reads as loose text with one boxed item in it.
                    'tw-block tw-w-full tw-cursor-pointer tw-rounded-xl tw-border-2 tw-border-solid tw-bg-white tw-p-4 tw-text-left tw-transition-colors hover:tw-border-success focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-accents-9',
                    // The green edge is the selected state everywhere in the app. No
                    // fill behind it: in a grid of a dozen tiles the washes are what
                    // turn a list of typefaces into a colour chart.
                    selected ? 'tw-border-success' : 'tw-border-accents-2',
                    compact && 'tw-p-3',
                    disabled && 'tw-cursor-not-allowed tw-opacity-50 hover:tw-border-accents-2',
                    className
                )}
                {...props}
            >
                {title && <span className="tw-block tw-text-sm tw-font-semibold tw-text-accents-9">{title}</span>}
                {children}
            </button>
        );
    }

    return (
        <div className={cx(base, compact && 'tw-p-3', className)} onClick={() => onSelect?.()} {...props}>
            <div className="tw-flex tw-items-center tw-gap-4">
                <Checkbox defaultSelected={selected} isSelected={selected} onChange={() => onSelect?.()} />
                {title && <span className="tw-text-lg">{title}</span>}
            </div>
            {children}
        </div>
    );
};

export default SelectableCard;
